import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send } from 'lucide-react';

const STORAGE_KEY = 'davetevibul_chat_session';
const TEASER_DISMISSED_KEY = 'davetevibul_chat_teaser_dismissed';
const POLL_INTERVAL_MS = 3000;
const TEASER_DELAY_MS = 8000;        // show teaser after 8s on page
const TEASER_AUTO_HIDE_MS = 30000;   // auto-hide teaser after 30s
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// WhatsApp customer line
const WHATSAPP_NUMBER_DISPLAY = '0850 307 71 97';
const WHATSAPP_URL = 'https://wa.me/908503077197';

// Friendly customer-service representative photo (Unsplash, free commercial use)
const AVATAR_PHOTO_URL =
  'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=85';

// Render message text and turn Turkish phone numbers (e.g. "0850 307 71 97") into a clickable WhatsApp link.
const renderMessageText = (text) => {
  if (!text) return null;
  // Match 4-3-2-2 grouped numbers like 0850 307 71 97 (with optional dashes)
  const phoneRegex = /(0\d{3}[\s-]?\d{3}[\s-]?\d{2}[\s-]?\d{2})/g;
  const parts = text.split(phoneRegex);
  return parts.map((part, i) => {
    if (phoneRegex.test(part)) {
      // Reset regex global lastIndex via clean variable; convert to wa.me link
      const digits = part.replace(/\D/g, '');
      const intlDigits = digits.startsWith('0') ? `90${digits.slice(1)}` : digits;
      return (
        <a
          key={i}
          href={`https://wa.me/${intlDigits}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold underline decoration-2 underline-offset-2 hover:opacity-80"
        >
          {part}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

const LiveChatWidget = () => {
  // Lazy-init from localStorage so we don't trigger setState inside an effect
  const restored = (() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
      if (stored) return JSON.parse(stored);
    } catch (e) { /* corrupt storage */ }
    return {};
  })();

  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState(restored.sessionId || null);
  const [visitorName, setVisitorName] = useState(restored.name || '');
  const [visitorPhone, setVisitorPhone] = useState(restored.phone || '');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showTeaser, setShowTeaser] = useState(false);

  const messagesEndRef = useRef(null);
  const lastFetchedAtRef = useRef(null);
  const pollTimerRef = useRef(null);

  // Show teaser bubble after delay (only if user hasn't dismissed it this session)
  useEffect(() => {
    if (isOpen) return;
    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(TEASER_DISMISSED_KEY) === '1';
    } catch (e) { /* ignore */ }
    if (dismissed) return;

    const showTimer = setTimeout(() => {
      setShowTeaser(true);
    }, TEASER_DELAY_MS);

    const hideTimer = setTimeout(() => {
      setShowTeaser(false);
    }, TEASER_DELAY_MS + TEASER_AUTO_HIDE_MS);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [isOpen]);

  const dismissTeaser = (e) => {
    if (e) e.stopPropagation();
    setShowTeaser(false);
    try {
      sessionStorage.setItem(TEASER_DISMISSED_KEY, '1');
    } catch (err) { /* ignore */ }
  };

  const openChat = () => {
    setIsOpen(true);
    setShowTeaser(false);
    try {
      sessionStorage.setItem(TEASER_DISMISSED_KEY, '1');
    } catch (err) { /* ignore */ }
  };

  const persistSession = (sid, name, phone) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId: sid, name, phone }));
    } catch (e) { /* ignore */ }
  };

  const pollMessages = useCallback(async () => {
    if (!sessionId) return;
    try {
      const since = lastFetchedAtRef.current;
      const url = new URL(`${BACKEND_URL}/api/chat/messages`);
      url.searchParams.set('sessionId', sessionId);
      if (since) url.searchParams.set('since', since);
      const res = await fetch(url.toString());
      if (!res.ok) return;
      const data = await res.json();
      if (data.success && Array.isArray(data.messages) && data.messages.length > 0) {
        setMessages(prev => {
          const next = [...prev];
          data.messages.forEach(m => {
            // Avoid duplicates:
            // 1) Exact match by createdAt+text (server echo of an already-stored msg)
            // 2) Recent optimistic visitor message — same text within last 15s — replace
            const exact = next.find(x => x.createdAt === m.createdAt && x.text === m.text);
            if (exact) return;
            if (m.sender === 'visitor') {
              const recent = next.find(x =>
                x.sender === 'visitor' &&
                x.text === m.text &&
                Math.abs(new Date(x.createdAt).getTime() - new Date(m.createdAt).getTime()) < 15000
              );
              if (recent) {
                recent.createdAt = m.createdAt; // sync timestamps to server
                return;
              }
            }
            next.push(m);
          });
          // Sort by createdAt to keep chronological order
          next.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          return next;
        });
        // Update fetch cursor to the latest message createdAt
        const latest = data.messages[data.messages.length - 1];
        lastFetchedAtRef.current = latest.createdAt;

        // Increment unread if window is closed and message is from agent
        if (!isOpen) {
          const agentMsgs = data.messages.filter(m => m.sender === 'agent');
          if (agentMsgs.length > 0) setUnreadCount(c => c + agentMsgs.length);
        }
      }
    } catch (e) {
      // silent — polling will retry
    }
  }, [sessionId, isOpen]);

  // Start polling whenever session exists
  useEffect(() => {
    if (!sessionId) return;
    // Defer first poll out of effect tick to satisfy strict hooks lint
    const firstPoll = setTimeout(() => pollMessages(), 0);
    pollTimerRef.current = setInterval(() => pollMessages(), POLL_INTERVAL_MS);
    return () => {
      clearTimeout(firstPoll);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [sessionId, pollMessages]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, isOpen]);

  // Reset unread on open
  useEffect(() => {
    if (isOpen) setUnreadCount(0);
  }, [isOpen]);

  const handleStart = async (e) => {
    e.preventDefault();
    if (!visitorName.trim()) return;
    setStarting(true);
    try {
      // Detect if the visitor is currently on a venue detail page.
      // VenueDetailPage sets window.__DAVET_VENUE_SLUG__ when a venue is loaded.
      const currentVenueSlug =
        (typeof window !== 'undefined' && window.__DAVET_VENUE_SLUG__) || '';
      const res = await fetch(`${BACKEND_URL}/api/chat/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: visitorName.trim(),
          phone: visitorPhone.trim(),
          pageUrl: window.location.href,
          venueSlug: currentVenueSlug
        })
      });
      const data = await res.json();
      if (data.success && data.sessionId) {
        setSessionId(data.sessionId);
        persistSession(data.sessionId, visitorName.trim(), visitorPhone.trim());
      }
    } catch (e) { /* ignore */ }
    setStarting(false);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !sessionId || sending) return;

    setSending(true);
    // Optimistic add
    const tempMsg = {
      sessionId,
      sender: 'visitor',
      text,
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMsg]);
    lastFetchedAtRef.current = tempMsg.createdAt;
    setDraft('');

    try {
      await fetch(`${BACKEND_URL}/api/chat/visitor-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, text })
      });
    } catch (e) { /* ignore */ }
    setSending(false);
  };

  return (
    <>
      {/* Floating launcher — circular avatar with optional teaser bubble */}
      {!isOpen && (
        <div className="fixed bottom-5 right-5 z-[60] flex items-end gap-3" data-testid="live-chat-launcher-wrap">
          {/* Teaser bubble (auto-appears after delay) */}
          {showTeaser && (
            <div
              className="relative mb-2 max-w-[240px] bg-white rounded-2xl rounded-br-sm shadow-xl border border-gray-200 px-4 py-3 pr-7 cursor-pointer animate-in fade-in slide-in-from-right-2 duration-300"
              onClick={openChat}
              data-testid="live-chat-teaser"
            >
              <button
                type="button"
                onClick={dismissTeaser}
                aria-label="Mesajı kapat"
                className="absolute top-1 right-1 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="text-sm text-gray-800 leading-snug">
                <span className="font-semibold text-rose-600">Merhaba!</span>{' '}
                DavetEviBul destek hattı — bir sorunuz mu var, yardımcı olalım?
              </div>
              {/* Tail */}
              <span
                className="absolute -bottom-1.5 right-4 w-3 h-3 bg-white border-r border-b border-gray-200 rotate-45"
                aria-hidden="true"
              />
            </div>
          )}

          {/* Avatar button */}
          <button
            type="button"
            onClick={openChat}
            aria-label="Canlı sohbeti aç"
            data-testid="live-chat-launcher"
            className="relative w-16 h-16 rounded-full overflow-visible shadow-xl shadow-rose-300/40 hover:shadow-2xl hover:shadow-rose-400/50 hover:-translate-y-0.5 transition-all ring-4 ring-white bg-white"
          >
            <img
              src={AVATAR_PHOTO_URL}
              alt="DavetEviBul müşteri temsilcisi"
              width="64"
              height="64"
              loading="lazy"
              className="w-full h-full rounded-full object-cover"
            />
            {/* Online indicator */}
            <span className="absolute bottom-0.5 right-0.5 flex h-3.5 w-3.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-green-500 ring-2 ring-white" />
            </span>
            {/* Unread badge */}
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          className="fixed bottom-5 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[380px] max-w-[380px] h-[560px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          data-testid="live-chat-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-rose-500 to-pink-500 text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <img
                  src={AVATAR_PHOTO_URL}
                  alt=""
                  width="40"
                  height="40"
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-white/40"
                />
                <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-400 ring-2 ring-rose-500" />
              </div>
              <div>
                <div className="font-semibold text-sm leading-tight">DavetEviBul Destek</div>
                <div className="text-xs opacity-90 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-300" />
                  Çevrimiçi
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Sohbeti kapat"
              data-testid="live-chat-close"
              className="p-1.5 rounded-full hover:bg-white/15 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          {!sessionId ? (
            <form onSubmit={handleStart} className="flex-1 flex flex-col p-5" data-testid="live-chat-start-form">
              <div className="text-sm text-gray-700 mb-4 leading-relaxed">
                Merhaba! Aradığınız davet evini birlikte bulalım. Sohbete başlamadan önce kısaca bilgileriniz:
              </div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Ad Soyad</label>
              <input
                type="text"
                placeholder="Adınız Soyadınız"
                value={visitorName}
                onChange={(e) => setVisitorName(e.target.value)}
                maxLength={60}
                required
                className="w-full h-11 px-3 mb-3 rounded-lg border-2 border-gray-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-100 outline-none text-sm"
                data-testid="live-chat-name-input"
              />
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                Telefon <span className="text-gray-400 normal-case font-normal">(opsiyonel)</span>
              </label>
              <input
                type="tel"
                placeholder="5XX XXX XX XX"
                value={visitorPhone}
                onChange={(e) => setVisitorPhone(e.target.value)}
                maxLength={20}
                className="w-full h-11 px-3 mb-5 rounded-lg border-2 border-gray-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-100 outline-none text-sm"
                data-testid="live-chat-phone-input"
              />
              <button
                type="submit"
                disabled={!visitorName.trim() || starting}
                className="h-11 rounded-lg bg-gradient-to-r from-rose-500 to-pink-500 text-white font-semibold text-sm hover:from-rose-600 hover:to-pink-600 disabled:opacity-50 transition-colors"
                data-testid="live-chat-start-button"
              >
                {starting ? 'Bağlanıyor...' : 'Sohbete Başla'}
              </button>
              <p className="mt-auto pt-3 text-[11px] text-gray-400 text-center">
                Bilgileriniz yalnızca destek amacıyla kullanılır.
              </p>
            </form>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 bg-gray-50 space-y-2.5" data-testid="live-chat-messages">
                {messages.length === 0 && (
                  <div className="text-center text-sm text-gray-400 py-8">
                    Mesajınızı yazın, kısa süre içinde yanıtlayalım.
                  </div>
                )}
                {messages.map((m, idx) => {
                  const isVisitor = m.sender === 'visitor';
                  const isSystem = m.sender === 'system';
                  if (isSystem) {
                    return (
                      <div key={idx} className="flex justify-center">
                        <div className="text-[11px] text-gray-500 bg-white border border-gray-200 px-3 py-1.5 rounded-full max-w-[85%] text-center">
                          {m.text}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={idx} className={`flex ${isVisitor ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[78%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                          isVisitor
                            ? 'bg-gradient-to-br from-rose-500 to-pink-500 text-white rounded-br-sm'
                            : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'
                        }`}
                      >
                        {!isVisitor && m.agentName && (
                          <div className="text-[10px] font-semibold text-rose-500 uppercase tracking-wider mb-0.5">
                            {m.agentName}
                          </div>
                        )}
                        {renderMessageText(m.text)}
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Composer */}
              <div className="border-t border-gray-200 bg-white">
                {/* Always-visible WhatsApp CTA */}
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-green-50 hover:bg-green-100 text-green-800 text-xs font-semibold transition-colors border-b border-green-100"
                  data-testid="live-chat-whatsapp-cta"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413"/>
                  </svg>
                  WhatsApp ile devam et: {WHATSAPP_NUMBER_DISPLAY}
                </a>
                <form onSubmit={handleSend} className="px-3 py-2.5 flex items-center gap-2" data-testid="live-chat-composer">
                  <input
                    type="text"
                    placeholder="Mesajınızı yazın..."
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    maxLength={1500}
                    className="flex-1 h-10 px-3 rounded-full bg-gray-100 border border-transparent focus:border-rose-300 focus:bg-white focus:ring-2 focus:ring-rose-100 outline-none text-sm"
                    data-testid="live-chat-input"
                  />
                  <button
                    type="submit"
                    disabled={!draft.trim() || sending}
                    className="w-10 h-10 rounded-full bg-gradient-to-r from-rose-500 to-pink-500 text-white flex items-center justify-center hover:from-rose-600 hover:to-pink-600 disabled:opacity-50 transition-colors"
                    aria-label="Gönder"
                    data-testid="live-chat-send-button"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default LiveChatWidget;
